"use client";

import { PlayIcon } from "@heroicons/react/24/solid";
import Image, { StaticImageData } from "next/image";
import { useState } from "react";
import YouTube, { YouTubeProps } from "react-youtube";

import { Modal } from "@/components/Modal";
import { Heading4 } from "@/components/text/Heading4";
import { SmallTitle } from "@/components/text/SmallTitle";
import { getSizesString } from "@/utils/getSizesString";

export function VideoUseCase({
  title,
  thumbnail,
  youtubeId,
  startTime,
  extraContent,
}: {
  title: string;
  thumbnail: StaticImageData;
  youtubeId: string;
  startTime: number;
  extraContent: React.ReactNode | null;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const opts: YouTubeProps["opts"] = {
    height: "100%",
    width: "100%",
    playerVars: {
      autoplay: 1,
      rel: 0,
      start: startTime,
    },
  };

  return (
    <>
      <div className="flex w-full flex-col items-center gap-8 rounded-3xl bg-primaryLight px-4 py-2">
        <div className="flex h-full w-full flex-row items-center justify-center gap-4">
          <button
            className="group block w-full max-w-[200px] flex-1 overflow-hidden rounded-3xl"
            onClick={() => setModalOpen(true)}
          >
            <div className="aspect-h-9 aspect-w-16 relative rounded-3xl">
              <Image
                src={thumbnail}
                alt={title}
                className="rounded-3xl object-cover"
                fill={true}
                sizes={getSizesString({
                  sm: 100,
                  md: 50,
                  lg: 33,
                  rest: 25,
                })}
              />

              <div className="absolute inset-0 flex flex-col items-center justify-center rounded-3xl">
                <span className="rounded-full">
                  <PlayIcon className="box-content h-6 w-6 rounded-full bg-gray-900 bg-opacity-80 fill-white p-3 transition-colors group-hover:bg-opacity-50" />
                </span>
                <SmallTitle
                  elementType="span"
                  className="block leading-tight text-white [text-shadow:1px_1px_2px_#000000] md:hidden"
                >
                  {title}
                </SmallTitle>
              </div>
            </div>
          </button>

          <div className="hidden w-full flex-1 flex-col items-center justify-center gap-y-2 md:flex">
            <Heading4 elementType="h3" className="mb-3 text-center">
              {title}
            </Heading4>
          </div>
        </div>
        {extraContent}
      </div>

      <Modal
        open={modalOpen}
        closeModal={() => setModalOpen(false)}
        widthMode="wide"
        title={title}
      >
        <div className="aspect-h-9 aspect-w-16 mx-auto">
          <YouTube videoId={youtubeId} opts={opts} />
        </div>
      </Modal>
    </>
  );
}
